import { QueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";

import { RetailShopFormData, RetailShopRes, ShopFull, ShopTiny } from "../types/retailShop";
import { getDomain } from "./cookies.core";
import { get, post } from "./fetch.core";

export interface RestrictionStatus {
  isRestrictedBool: boolean;
  reason: "ip" | "time" | null;
  clientIp?: string;
}

export async function isPosShopRestricted(id: any): Promise<RestrictionStatus> {
  const res = await get<RestrictionStatus>(`/pos/retailShop/isRestricted?id=${id}`);
  return res.data;
}

export async function restrictionRole(id: string) {
  const res = await get<any>(`/pos/restriction/role?id=${id}`);
  return res.data;
}

export function getLoginToken() {
  return Cookies.get("loginTokenAdmin");
}

export function removeLoginTokenAndShopId() {
  Cookies.remove("loginTokenAdmin", { domain: getDomain() });
  clearShopRetailId();
}

export function getShopRetailId(): { shopId: string; retailId: string } | null {
  let combined: string | undefined;

  if (typeof window !== "undefined") {
    combined = sessionStorage.getItem("shopRetailId") ?? Cookies.get("shopRetailId");
  } else {
    combined = Cookies.get("shopRetailId");
  }

  // Migration: If no combined cookie, try to build from old cookies
  if (!combined) return null;

  const [shopId, retailId] = combined.split("_");

  // Validate format - should be MongoDB ObjectIds (24 hex characters)
  if (!shopId || !retailId || !/^[a-f\d]{24}$/i.test(shopId) || !/^[a-f\d]{24}$/i.test(retailId)) {
    console.warn("Invalid shopRetailId format, clearing cookie");
    clearShopRetailId();
    return null;
  }

  return { shopId, retailId };
}

export function setShopRetailId(shopId: string, retailId: string): void {
  const combined = `${shopId}_${retailId}`;

  // Set in BOTH cookies (cross-tab) and sessionStorage (tab-specific)
  Cookies.set("shopRetailId", combined, {
    domain: getDomain(),
    expires: 365,
    sameSite: "strict",
  });

  if (typeof window !== "undefined") {
    sessionStorage.setItem("shopRetailId", combined);
  }
}

export function clearShopRetailId(): void {
  Cookies.remove("shopRetailId", { domain: getDomain() });
  if (typeof window !== "undefined") {
    sessionStorage.removeItem("shopRetailId");
  }
}

// Helper functions for backward compatibility with existing code
export function getShopId(): string | undefined {
  return getShopRetailId()?.shopId;
}

export function getRetailShopId(): string | undefined {
  return getShopRetailId()?.retailId;
}

// Simplify since both IDs are immediately available
export async function getRetailShopIdWait() {
  return Promise.resolve(getRetailShopId());
}

export async function logout(queryClient: QueryClient) {
  Cookies.remove("loginTokenAdmin", { domain: getDomain() });
  clearShopRetailId(); // Single call replaces both clearShopId() and clearRetailShopId()

  queryClient.invalidateQueries({ queryKey: ["me"] });

  window.location.href = process.env.NEXT_PUBLIC_ADMIN_URL + "/login" + "?route=" + process.env.NEXT_PUBLIC_POS_URL;
}

export async function listRetailShops() {
  const res = await get<RetailShopRes[]>("/pos/retailShop/list", {
    locale: undefined,
    useRetailShopId: false,
    useShopId: true,
    useToken: true,
  });
  return res.data;
}

export async function getSinglePos(id: string) {
  const res = await get<RetailShopRes>(`/pos/retailShop/single?id=${id}`);
  return res.data;
}

export async function getAdminShop() {
  const res = await get<ShopFull>("/admin/shop/get");
  return res.data;
}

export async function getPosShopList() {
  const res = await get<RetailShopRes[]>("/pos/retailShop/list");
  return res.data;
}

export async function updatePosShop(id: string, data: RetailShopFormData) {
  const res = await post("/pos/retailShop/update", {
    id,
    ...data,
  });

  return res.data;
}
